// DESIGN: Subscriber-safe console metrics aggregate work signals, plan, and
// credit context. They intentionally exclude raw Mission Control Fire topology,
// provider routing, credentials, vendor spend, margin, and mutation controls.
import React from 'react';
import { Activity, ArrowUpRight, CheckCircle2, CreditCard, FolderKanban, ShieldCheck } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { apiFetch, authHeaders } from '../api';
import type { ProjectSummary } from '../api';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../useApi';

export type CostBucket = {
  period: string;
  byProject?: Record<string, number>;
  byLine?: { forge?: number; factory?: number };
  total?: number;
};
export type Cost = {
  buckets?: CostBucket[];
  totals?: Record<string, number>;
  derived?: {
    burnRatePerDay?: number;
    avgCostPerBuild?: number;
    avgCostPerForgeRun?: number;
    factoryVsForgeSplit?: { factory?: number; forge?: number };
  };
};

type Findings = { buckets?: Array<{ period: string; open: number }>; open?: number; resolved?: number; total?: number };
export type Caps = {
  anyCapSet?: boolean;
  perBuildCaps?: Array<{ buildId: string; projectId: string; capUsd: number }>;
  // subscriber_cost_caps raw rows (typically empty — most subscribers are pay-as-you-go).
  windowedCaps?: Array<{ monthly_usd?: number | null; weekly_usd?: number | null; daily_usd?: number | null; hourly_usd?: number | null; per_task_usd?: number | null }>;
};

export const money = (n?: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);
/** Reused by the Dashboard landing page (ocn-S657-P0) KPI strip — never duplicate this query logic. */
export async function metric<T>(path: string): Promise<T> {
  const headers = await authHeaders();
  const response = await apiFetch(`${import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev'}/api/subscriber/metrics/${path}`, { headers });
  if (!response.ok) throw new Error('Metrics are unavailable');
  return response.json();
}

/** Keeps a metrics rendering failure contained to this page rather than the whole portal. */
class MetricsErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[MetricsErrorBoundary] Metrics page failed to render:', error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <div className="card empty-state" role="alert"><strong>Metrics couldn't load</strong><div>Please try again later.</div></div>;
    }
    return this.props.children;
  }
}

type MetricsPageProps = {
  projects?: ProjectSummary[];
  onNavigate?: (page: string) => void;
};

function MetricsPageContent({ projects = [], onNavigate }: MetricsPageProps) {
  const { subscriber } = useAuth();
  const { data: findings } = useApi(() => metric<Findings>('findings?scope=me'), []);
  const { data: caps } = useApi(() => metric<Caps>('caps?scope=me'), []);
  const findingsBuckets = findings?.buckets ?? [];
  const perBuildCaps = caps?.perBuildCaps ?? [];
  const projectCount = projects.length;
  const creditBalance = subscriber?.credit_balance_remaining;
  const planLabel = subscriber?.plan ? `${subscriber.plan} plan` : 'Plan details unavailable';
  const planStatus = subscriber?.status ? subscriber.status.replace(/_/g, ' ') : 'account status unavailable';
  const resetLabel = subscriber?.token_balance_reset_at
    ? `Resets ${new Date(subscriber.token_balance_reset_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : 'Usage cycle managed in account';

  const StatCard = ({ label, value, detail, Icon, tone }: { label: string; value: string; detail: string; Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>; tone: string }) => (
    <section className="card" style={{ minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderTop: `2px solid ${tone}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div className="section-label" style={{ margin: 0 }}>{label}</div>
        <span style={{ color: tone, display: 'inline-flex', padding: 6, borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, currentColor 10%, transparent)' }}><Icon size={16} strokeWidth={1.8} /></span>
      </div>
      <div>
        <div className="stat-value" style={{ margin: '8px 0 4px' }}>{value}</div>
        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>{detail}</div>
      </div>
    </section>
  );

  return <div>
    <div className="page-title-row" style={{ gap: 20, flexWrap: 'wrap' }}>
      <div>
        <div className="section-label" style={{ color: 'var(--accent)', marginBottom: 7 }}>Subscriber workspace</div>
        <h2 className="page-title" style={{ fontSize: 'clamp(22px, 3vw, 30px)', marginBottom: 7 }}>Work activity &amp; usage</h2>
        <p className="page-subtitle" style={{ maxWidth: 650 }}>A clear view of work signals, account capacity, and plan context for this workspace.</p>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, alignSelf: 'flex-start', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
        <ShieldCheck size={14} color="var(--green)" /> Subscriber-safe view
      </div>
    </div>

    <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', marginBottom: 16, border: '1px solid var(--border)', borderLeft: '2px solid var(--accent)', borderRadius: 'var(--radius-sm)', background: 'var(--surface2)', color: 'var(--text-muted)', fontSize: 'var(--text-xs)', lineHeight: 1.55 }}>
      <ShieldCheck size={15} color="var(--accent)" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>This is an OCNexus workspace summary. It does not expose raw Mission Control Fires, execution topology, provider routing, credentials, or internal cost telemetry.</span>
    </div>

    <div className="metrics-kpi-grid" style={{ marginBottom: 16 }}>
      <StatCard label="Connected projects" value={String(projectCount)} detail={projectCount === 1 ? 'project in this workspace' : 'projects in this workspace'} Icon={FolderKanban} tone="var(--accent)" />
      <StatCard label="Needs attention" value={String(findings?.open ?? 0)} detail="open work signals" Icon={Activity} tone="var(--orange)" />
      <StatCard label="Resolved" value={String(findings?.resolved ?? 0)} detail="work signals closed" Icon={CheckCircle2} tone="var(--green)" />
      <StatCard label="Usage guardrails" value={caps?.anyCapSet ? 'Active' : 'Standard'} detail={caps?.anyCapSet ? `${perBuildCaps.length} project limit${perBuildCaps.length === 1 ? '' : 's'} configured` : 'plan-level controls apply'} Icon={CreditCard} tone="var(--purple)" />
    </div>

    <div className="metrics-detail-grid">
      <section className="card" style={{ minHeight: 306, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div className="section-label">Work signal trend</div>
            <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>Open signals are aggregated for your authorized projects.</div>
          </div>
          <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>{findings?.total ?? 0} observed</div>
        </div>
        {findingsBuckets.length ? <div style={{ height: 218, marginTop: 'auto' }}><ResponsiveContainer><LineChart data={findingsBuckets} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}><CartesianGrid stroke="var(--border)" vertical={false} /><XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} /><YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} /><Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }} labelStyle={{ color: 'var(--text-muted)' }} /><Line type="monotone" dataKey="open" name="Open signals" stroke="var(--accent)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} /></LineChart></ResponsiveContainer></div> : <div className="empty-state" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'left' }}><div><strong style={{ color: 'var(--text)' }}>No work signals yet</strong><div className="muted" style={{ marginTop: 5 }}>As work is created and completed, this workspace trend will appear here.</div></div></div>}
      </section>

      <section className="card" style={{ minHeight: 306, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div><div className="section-label">Usage &amp; plan</div><div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>Commercial context for this account.</div></div>
          <CreditCard size={16} color="var(--purple)" />
        </div>
        <div style={{ display: 'grid', gap: 11, margin: '18px 0' }}>
          <div style={{ paddingBottom: 10, borderBottom: '1px solid var(--border)' }}><div className="muted" style={{ fontSize: 'var(--text-xs)' }}>Current plan</div><div style={{ color: 'var(--text)', fontWeight: 'var(--weight-semibold)', marginTop: 3, textTransform: 'capitalize' }}>{planLabel}</div><div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2, textTransform: 'capitalize' }}>{planStatus}</div></div>
          <div style={{ paddingBottom: 10, borderBottom: '1px solid var(--border)' }}><div className="muted" style={{ fontSize: 'var(--text-xs)' }}>Credits available</div><div style={{ color: 'var(--text)', fontWeight: 'var(--weight-semibold)', marginTop: 3 }}>{creditBalance == null ? '—' : creditBalance.toLocaleString()}</div><div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>{resetLabel}</div></div>
          <div><div className="muted" style={{ fontSize: 'var(--text-xs)' }}>Billing records</div><div style={{ color: 'var(--text-dim)', fontSize: 'var(--text-sm)', marginTop: 4, lineHeight: 1.45 }}>Invoices, receipts, adjustments, and settlement history remain in your account billing experience.</div></div>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => onNavigate?.('settings')} style={{ marginTop: 'auto', width: 'fit-content' }}>Account &amp; support <ArrowUpRight size={14} /></button>
      </section>
    </div>

    <section className="card" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderLeft: '2px solid var(--orange)' }}>
      <div><div className="section-label">Need the detail behind a signal?</div><div className="muted" style={{ marginTop: 5, fontSize: 'var(--text-sm)' }}>Use Chat to inspect work in context. Individual records remain scoped to your workspace and approved artifacts.</div></div>
      <button type="button" className="btn btn-primary" onClick={() => onNavigate?.('subscriber-chat')}>Open Chat <ArrowUpRight size={14} /></button>
    </section>
  </div>;
}

export function MetricsPage({ projects = [], onNavigate }: MetricsPageProps) {
  return <MetricsErrorBoundary><MetricsPageContent projects={projects} onNavigate={onNavigate} /></MetricsErrorBoundary>;
}
